import {useCallback, useEffect, useMemo, useState} from 'react'
import {useNavigate, useParams} from 'react-router-dom'
import axios from 'axios'
import {PageTitle} from '../../../_metronic/layout/core'
import {KTCardBody} from '../../../_metronic/helpers'
import {apiUrl} from '../../../api-utils/constantVariables'
import {AdminAuth} from '../../modules/auth/core/AdminAuth'
import {useCan} from '../../modules/auth/core/AdminPermissions'

type Perm = {
  id: number
  code: string
  resource: string
  action: string
  label: string
}

type RoleDetail = {
  id: number
  code: string
  name: string
  description?: string | null
  is_system?: boolean
  permissions?: Perm[]
}

const RoleEditor = () => {
  const {id} = useParams()
  const nav = useNavigate()
  const canEdit = useCan('roles.edit')
  const canDelete = useCan('roles.delete')
  const [role, setRole] = useState<RoleDetail | null>(null)
  const [catalog, setCatalog] = useState<Perm[]>([])
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState('')

  const permissionsReadOnly = role?.code === 'super_admin' || !canEdit
  const metaReadOnly = !canEdit

  const load = useCallback(async () => {
    const token = AdminAuth()
    if (!token || !id) return
    setLoading(true)
    setErr('')
    try {
      const [rRes, pRes] = await Promise.all([
        axios.get(`${apiUrl}/admin/roles/${id}`, {headers: {'x-access-token': token}}),
        axios.get(`${apiUrl}/admin/permissions`, {headers: {'x-access-token': token}}),
      ])
      const r = rRes.data?.data as RoleDetail
      const perms = pRes.data?.data as Perm[] | undefined
      setRole(r || null)
      setName(r?.name || '')
      setDescription(r?.description || '')
      const list = Array.isArray(perms) ? perms : []
      setCatalog(list.sort((a, b) => a.code.localeCompare(b.code)))
      const granted = new Set<number>((r?.permissions || []).map((p) => p.id))
      setSelected(granted)
    } catch (e: any) {
      setErr(e?.response?.data?.message || 'Failed to load role.')
      setRole(null)
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    void load()
  }, [load])

  const toggle = (permId: number) => {
    if (permissionsReadOnly) return
    setSelected((prev) => {
      const n = new Set(prev)
      if (n.has(permId)) n.delete(permId)
      else n.add(permId)
      return n
    })
  }

  const saveMeta = async () => {
    if (!role || metaReadOnly) return
    const token = AdminAuth()
    setSaving(true)
    setErr('')
    try {
      await axios.patch(
        `${apiUrl}/admin/roles/${role.id}`,
        {name: name.trim(), description: description.trim() || undefined},
        {headers: {'x-access-token': token}}
      )
      await load()
    } catch (e: any) {
      setErr(e?.response?.data?.message || 'Update failed.')
    } finally {
      setSaving(false)
    }
  }

  const savePermissions = async () => {
    if (!role || permissionsReadOnly) return
    const token = AdminAuth()
    setSaving(true)
    setErr('')
    try {
      const permission_ids: number[] = []
      selected.forEach((id) => permission_ids.push(id))
      await axios.put(
        `${apiUrl}/admin/roles/${role.id}/permissions`,
        {permission_ids},
        {headers: {'x-access-token': token}}
      )
      await load()
    } catch (e: any) {
      setErr(e?.response?.data?.message || 'Could not save permissions.')
    } finally {
      setSaving(false)
    }
  }

  const deleteRole = async () => {
    if (!role || role.is_system) return
    if (!window.confirm(`Delete role “${role.name}”? This cannot be undone.`)) return
    const token = AdminAuth()
    setSaving(true)
    setErr('')
    try {
      await axios.delete(`${apiUrl}/admin/roles/${role.id}`, {
        headers: {'x-access-token': token},
      })
      nav('/settings/roles')
    } catch (e: any) {
      setErr(e?.response?.data?.message || 'Delete failed.')
    } finally {
      setSaving(false)
    }
  }

  const grouped = useMemo(() => {
    const m = new Map<string, Perm[]>()
    for (const p of catalog) {
      const k = p.resource || 'other'
      if (!m.has(k)) m.set(k, [])
      m.get(k)!.push(p)
    }
    const keys: string[] = []
    m.forEach((_perms, key) => keys.push(key))
    keys.sort((a, b) => a.localeCompare(b))
    return keys.map((key) => [key, m.get(key)!] as [string, Perm[]])
  }, [catalog])

  if (loading) {
    return (
      <>
        <PageTitle breadcrumbs={[]}>Role</PageTitle>
        <div className='p-10'>Loading…</div>
      </>
    )
  }

  if (!role) {
    return (
      <>
        <PageTitle breadcrumbs={[]}>Role</PageTitle>
        <div className='alert alert-danger m-5'>{err || 'Role not found.'}</div>
      </>
    )
  }

  return (
    <>
      <PageTitle breadcrumbs={[]}>{role.name}</PageTitle>
      <div className='d-flex flex-wrap gap-3 mb-5'>
        <button type='button' className='btn btn-light' onClick={() => nav('/settings/roles')}>
          ← All roles
        </button>
        {canDelete && !role.is_system ? (
          <button
            type='button'
            className='btn btn-light-danger'
            disabled={saving}
            onClick={deleteRole}
          >
            Delete role
          </button>
        ) : null}
      </div>

      {err ? <div className='alert alert-danger'>{err}</div> : null}

      {permissionsReadOnly && role.code === 'super_admin' ? (
        <div className='alert alert-info'>
          Super Admin permissions are fixed (full access). You can still edit display name and
          description.
        </div>
      ) : null}

      <div className='card mb-5'>
        <div className='card-header'>
          <h3 className='card-title'>Details</h3>
        </div>
        <KTCardBody>
          <div className='row'>
            <div className='col-md-6 mb-4'>
              <label className='form-label'>Name</label>
              <input
                className='form-control'
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={metaReadOnly}
              />
            </div>
            <div className='col-md-6 mb-4'>
              <label className='form-label'>Code</label>
              <input className='form-control' value={role.code} disabled />
            </div>
            <div className='col-12 mb-4'>
              <label className='form-label'>Description</label>
              <textarea
                className='form-control'
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={metaReadOnly}
              />
            </div>
          </div>
          {!metaReadOnly ? (
            <button
              type='button'
              className='btn btn-light-primary'
              disabled={saving}
              onClick={saveMeta}
            >
              Save details
            </button>
          ) : null}
        </KTCardBody>
      </div>

      <div className='card'>
        <div className='card-header'>
          <h3 className='card-title'>Permissions</h3>
        </div>
        <KTCardBody>
          {grouped.map(([resource, perms]) => (
            <div key={resource} className='mb-10'>
              <h4 className='fs-5 text-gray-800 mb-4 text-capitalize'>
                {resource.replace(/_/g, ' ')}
              </h4>
              <div className='table-responsive'>
                <table className='table table-row-bordered align-middle'>
                  <thead>
                    <tr className='text-muted fw-semibold fs-7 text-uppercase'>
                      <th>Allow</th>
                      <th>Label</th>
                      <th>Action</th>
                      <th>Code</th>
                    </tr>
                  </thead>
                  <tbody>
                    {perms
                      .sort((a: Perm, b: Perm) => a.action.localeCompare(b.action))
                      .map((p: Perm) => (
                        <tr key={p.id}>
                          <td style={{width: 72}}>
                            <div className='form-check form-check-custom form-check-solid'>
                              <input
                                className='form-check-input'
                                type='checkbox'
                                checked={selected.has(p.id)}
                                onChange={() => toggle(p.id)}
                                disabled={permissionsReadOnly}
                              />
                            </div>
                          </td>
                          <td>{p.label}</td>
                          <td>{p.action}</td>
                          <td>
                            <code className='fs-8'>{p.code}</code>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
          {!permissionsReadOnly ? (
            <button
              type='button'
              className='btn btn-primary'
              disabled={saving}
              onClick={savePermissions}
            >
              Save permissions
            </button>
          ) : null}
        </KTCardBody>
      </div>
    </>
  )
}

export default RoleEditor
